//npm
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DropDownModule } from '@bayclubs/drop-down';
import { TestIdDirective } from '@bayclubs/horizon';
import { BayclubsUiDatePickerModule } from '@bayclubs/ui-date-picker';
import { BayclubsUiTimePickerModule } from '@bayclubs/ui-time-picker';
import { UtilsModule } from '@bayclubs/utils';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgxUploaderModule } from 'ngx-uploader';
import { PurchaseDescriptionPipe } from './components/enrollment-info-tile/purchase-description.pipe';
import { TranslatePurchaseStatusPipe } from './components/enrollment-info-tile/translate-purchase-status.pipe';

//application components
import { NavbarComponent } from './components/navbar/navbar.component';
import { DashboardComponent } from './routes/dashboard/dashboard.component';
import { LoginComponent } from './routes/login/login.component';

//application modules
import { InfrastructureModule } from '../infrastructure/infrastructure.module';

//application services
import { CampCareBookingsCalendarEventService } from './routes/camp-care-booking/camp-care-booking-calendar/camp-care-bookings-calendar-event.service';
import { DashboardFilterService } from './routes/dashboard/dashboard-filter/dashboard-filter.service';
import { PurchaseEditEventService } from './routes/purchase-list/purchase-edit/purchase-edit-event.service';
import { SessionsWeeksService } from './routes/purchase-list/session-weeks.service';
import { CampCareApiService } from './services/api/camp-care-api.service';
import { CampCareBookingApiService } from './services/api/camp-care-booking-api.service';
import { CampEnrollmentApiService } from './services/api/camp-enrollment-api.service';
import { CartApiService } from './services/api/cart-api.service';
import { ChildApiService } from './services/api/child-api.service';
import { CouponApiService } from './services/api/coupon-api.service';
import { PurchaseApiService } from './services/api/purchase-api.service';
import { ReportsApiService } from './services/api/reports-api.service';
import { RosterApiService } from './services/api/roster-api.service';
import { SellCampApiService } from './services/api/sell-camp-api.service';
import { StatisticsApiService } from './services/api/statistics-api.service';
import { CampsEnumTranslationService } from './services/camps-enum-translation.service';

//application components
import { UiSwitchModule } from 'ngx-ui-switch';
import { CampIconComponent } from './components/camp-icon/camp-icon.component';
import { ClubSelectComponent } from './components/club-select/club-select.component';
import { EnrollmentInfoTileComponent } from './components/enrollment-info-tile/enrollment-info-tile.component';
import { LoggedUserComponent } from './components/logged-user/logged-user.component';
import { MembershipSearchComponent } from './components/membership-search/membership-search.component';
import { SideMenuComponent } from './components/side-menu/side-menu.component';
import { CampEnrollmentComponent } from './routes/camp-enrollment/camp-enrollment.component';
import { SessionSearchCriteriaComponent } from './routes/camp-enrollment/session-search-criteria/session-search-criteria.component';
import { CampCreateComponent } from './routes/camp-management/camp-create/camp-create.component';
import { CampBasicInfoComponent } from './routes/camp-management/camp-details/camp-basic-info/camp-basic-info.component';
import { CampBasicInfoEditComponent } from './routes/camp-management/camp-details/camp-basic-info/edit/camp-basic-info-edit.component';
import { CampCareDayEditComponent } from './routes/camp-management/camp-details/camp-care/camp-care-day-edit/camp-care-day-edit.component';
import { CampCareDaySingleEditComponent } from './routes/camp-management/camp-details/camp-care/camp-care-day-single-edit/camp-care-day-single-edit.component';
import { CampCareDaysGeneratorSummaryComponent } from './routes/camp-management/camp-details/camp-care/camp-care-days-generator/camp-care-days-generator-summary/camp-care-days-generator-summary.component';
import { CampCareDaysGeneratorComponent } from './routes/camp-management/camp-details/camp-care/camp-care-days-generator/camp-care-days-generator.component';
import { CampCareDaysListComponent } from './routes/camp-management/camp-details/camp-care/camp-care-days-list/camp-care-days-list.component';
import { CampCareSlotsEditComponent } from './routes/camp-management/camp-details/camp-care/camp-care-slots-edit/camp-care-slots-edit.component';
import { CampCareComponent } from './routes/camp-management/camp-details/camp-care/camp-care.component';
import { CampCategoryComponent } from './routes/camp-management/camp-details/camp-category/camp-category.component';
import { CampCategoryEditComponent } from './routes/camp-management/camp-details/camp-category/edit/camp-category-edit.component';
import { CampsDetailsFormBuilder } from './routes/camp-management/camp-details/camp-details-form-builder';
import { CampsDetailsFormStateService } from './routes/camp-management/camp-details/camp-details-form-state-service';
import { CampDetailsComponent } from './routes/camp-management/camp-details/camp-details.component';
import { CampVisibilityComponent } from './routes/camp-management/camp-details/camp-visibility/camp-visibility.component';
import { CampVisibilityEditComponent } from './routes/camp-management/camp-details/camp-visibility/edit/camp-visibility-edit.component';
import { DiscountDetailsComponent } from './routes/camp-management/camp-details/discounts-list/discount-details/discount-details.component';
import { DiscountDetailsEditComponent } from './routes/camp-management/camp-details/discounts-list/discount-details/edit/discount-details-edit.component';
import { DiscountsListComponent } from './routes/camp-management/camp-details/discounts-list/discounts-list.component';
import { SessionsGroupComponent } from './routes/camp-management/camp-details/sessions-group/sessions-group.component';
import { DayVariantDetailsComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/day-variants-list/day-variant-details/day-variant-details.component';
import { DayVariantDetailsEditComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/day-variants-list/day-variant-details/edit/day-variant-details-edit.component';
import { DayVariantsListComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/day-variants-list/day-variants-list.component';
import { OptionDetailsEditComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/options-list/option-details/edit/option-details-edit.component';
import { OptionDetailsComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/options-list/option-details/option-details.component';
import { OptionsListComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/options-list/options-list.component';
import { SessionAddComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/session-basic-info/add/session-add.component';
import { SessionsBasicInfoEditComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/session-basic-info/edit/session-basic-info-edit.component';
import { SessionsBasicInfoComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/session-basic-info/session-basic-info.component';
import { SessionDaysComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/session-days/session-days.component';
import { SessionsDetailsComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/session-details.component';
import { TimeVariantDetailsComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/time-variants-list/time-variant-details/time-variant-details.component';
import { TimeVariantsListComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/time-variants-list/time-variants-list.component';
import { SessionsListComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/sessions-list.component';
import { CampListRowComponent } from './routes/camp-management/camp-list/camp-list-row/camp-list-row.component';
import { CampListComponent } from './routes/camp-management/camp-list/camp-list.component';
import { CampSearchCriteriaComponent } from './routes/camp-management/camp-list/camp-search-criteria/camp-search-criteria.component';
import { SortCampsComponent } from './routes/camp-management/sort-camps/sort-camps.component';
import { DailyAmountChartComponent } from './routes/dashboard/daily-amount-chart/daily-amount-chart.component';
import { DashboardFilterComponent } from './routes/dashboard/dashboard-filter/dashboard-filter.component';
import { StatisticsChartComponent } from './routes/dashboard/statistics-chart/statistics-chart.component';
import { StatisticsTilesComponent } from './routes/dashboard/statistics-tiles/statistics-tiles.component';
import { ForbiddenComponent } from './routes/forbidden/forbidden.component';
import { AcquiredSessionItemComponent } from './routes/purchase-list/purchase-details/acquired-sessions/acquired-session-item/acquired-session-item.component';
import { AcquiredSessionsComponent } from './routes/purchase-list/purchase-details/acquired-sessions/acquired-sessions.component';
import { PaymentDetailsComponent } from './routes/purchase-list/purchase-details/payment-details/payment-details.component';
import { PurchaseDetailsComponent } from './routes/purchase-list/purchase-details/purchase-details.component';
import { PurchaseInfoComponent } from './routes/purchase-list/purchase-details/purchase-info/purchase-info.component';
import { PurchaseListRowComponent } from './routes/purchase-list/purchase-list-row/purchase-list-row.component';
import { PurchaseListComponent } from './routes/purchase-list/purchase-list.component';
import { PurchaseSearchCriteriaComponent } from './routes/purchase-list/purchase-search-criteria/purchase-search-criteria.component';
import { CampDefinitionApiService } from './services/api/camp-definition-api.service';
import { FilterApiService } from './services/api/filter-api.service';
import { MembershipApiService } from './services/api/membership-api.service';

import { CampCareBookingCalendarBodyComponent } from './routes/camp-care-booking/camp-care-booking-calendar/camp-care-booking-calendar-body/camp-care-booking-calendar-body.component';
import { CampCareBookingCalendarNavComponent } from './routes/camp-care-booking/camp-care-booking-calendar/camp-care-booking-calendar-nav/camp-care-booking-calendar-nav.component';
import { CampCareBookingCalendarComponent } from './routes/camp-care-booking/camp-care-booking-calendar/camp-care-booking-calendar.component';
import { CampCareBookingCriteriaComponent } from './routes/camp-care-booking/camp-care-booking-criteria/camp-care-booking-criteria.component';
import { CampCareBookingComponent } from './routes/camp-care-booking/camp-care-booking.component';
import { DaysVariantEnrollmentItemComponent } from './routes/camp-enrollment/days-variant-enrollment-item/days-variant-enrollment-item.component';
import { SessionEnrollmentItemComponent } from './routes/camp-enrollment/session-enrollment-item/session-enrollment-item.component';
import { CampPricingComponent } from './routes/camp-management/camp-details/camp-pricing/camp-pricing.component';
import { PricingMatrixComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/pricing-matrix/pricing-matrix.component';
import { CouponDetailsComponent } from './routes/coupons/coupon-details/coupon-details.component';
import { CouponDetailsModalComponent } from './routes/coupons/coupon-details/modal/coupon-details.modal.component';
import { CouponGenerateComponent } from './routes/coupons/coupon-generator/coupon-generate.component';
import { CouponGenerateModalComponent } from './routes/coupons/coupon-generator/modal/coupon-generate.component.modal';
import { ConfirmationReasonComponent } from './routes/coupons/coupons-list/confirmation-reason/confirmation-reason.component';
import { CouponListRowComponent } from './routes/coupons/coupons-list/coupon-list-row/coupon-list-row.component';
import { CouponsListComponent } from './routes/coupons/coupons-list/coupons-list.component';
import { CouponsSearchCriteriaComponent } from './routes/coupons/coupons-list/coupons-search-criteria/coupons-search-criteria.component';
import { AcquiredSessionsSummaryComponent } from './routes/purchase-list/purchase-details/acquired-sessions/acquired-sessions-summary/acquired-sessions-summary.component';
import { PurchaseEditInfoComponent } from './routes/purchase-list/purchase-edit/purchase-edit-info/purchase-edit-info.component';
import { PurchaseEditSessionSummaryItemComponent } from './routes/purchase-list/purchase-edit/purchase-edit-sessions-summary/purchase-edit-session-summary-item/purchase-edit-session-summary-item.component';
import { PurchaseEditSessionsSummaryComponent } from './routes/purchase-list/purchase-edit/purchase-edit-sessions-summary/purchase-edit-sessions-summary.component';
import { PurchaseEditSessionItemComponent } from './routes/purchase-list/purchase-edit/purchase-edit-sessions/purchase-edit-session-item/purchase-edit-session-item.component';
import { PurchaseEditSessionsComponent } from './routes/purchase-list/purchase-edit/purchase-edit-sessions/purchase-edit-sessions.component';
import { PurchaseEditSummaryComponent } from './routes/purchase-list/purchase-edit/purchase-edit-summary/purchase-edit-summary.component';
import { PurchaseEditComponent } from './routes/purchase-list/purchase-edit/purchase-edit.component';
import { PurchaseNewSessionModalComponent } from './routes/purchase-list/purchase-edit/purchase-new-session-modal/purchase-new-session-modal.component';
import { RosterEventService } from './routes/roster/roster-event.service';
import { RosterItemComponent } from './routes/roster/roster-item/roster-item.component';
import { RosterSearchCriteriaComponent } from './routes/roster/roster-search-criteria/roster-search-criteria.component';
import { RosterComponent } from './routes/roster/roster.component';
import { CampManagementApiService } from './services/api/camp-management-api.service';
import { DayVariantManagementApiService } from './services/api/day-variant-management-api.service';
import { DiscountManagementApiService } from './services/api/discount-management-api.service';
import { OptionManagementApiService } from './services/api/option-management-api.service';
import { PaymentApiService } from './services/api/payment-api.service';
import { SessionManagementApiService } from './services/api/session-management-api.service';
import { WaiveApiService } from './services/api/waive-api.service';

import { BC_PRIVILEGES_PROVIDERS, BcEmployeeAuthModule } from '@bayclubs/employee-auth';
import { NgxMaskDirective } from 'ngx-mask';
import { MemberSearchComponent } from './components/member-search/member-search.component';
import { SessionDaysShiftComponent } from './routes/camp-management/camp-details/sessions-group/sessions-list/session-details/session-days-shift/session-days-shift.component';
import { CampImportComponent } from './routes/camp-management/camp-import/camp-import.component';
import { CampOptionsSelectorComponent } from './routes/manage-images/camp-options-selector/camp-options-selector.component';
import { CampTypeSectionComponent } from './routes/manage-images/category-image-manager/camp-type-section/camp-type-section.component';
import { CategoryImageManagerComponent } from './routes/manage-images/category-image-manager/category-image-manager.component';
import { ManageImagesComponent } from './routes/manage-images/manage-images.component';
import { MembershipListRowComponent } from './routes/membership-list/membership-list-row/membership-list-row.component';
import { MembershipListComponent } from './routes/membership-list/membership-list.component';
import { MembershipSearchCriteriaComponent } from './routes/membership-list/membership-search-criteria/membership-search-criteria.component';
import { RefundedSessionsComponent } from './routes/purchase-list/purchase-details/refunded-sessions/refunded-sessions.component';
import { CapacityReportItemComponent } from './routes/reports/capacity-report/capacity-report-item/capacity-report-item.component';
import { CapacityReportSearchCriteriaComponent } from './routes/reports/capacity-report/capacity-report-search-criteria/capacity-report-search-criteria.component';
import { CapacityReportComponent } from './routes/reports/capacity-report/capacity-report.component';
import { EnrollmentReportItemComponent } from './routes/reports/enrollment-report/enrollment-report-item/enrollment-report-item.component';
import { EnrollmentReportSearchCriteriaComponent } from './routes/reports/enrollment-report/enrollment-report-search-criteria/enrollment-report-search-criteria.component';
import { EnrollmentReportComponent } from './routes/reports/enrollment-report/enrollment-report.component';
import { ReportSelectComponent } from './routes/reports/report-select/report-select.component';
import { ReportsEventService } from './routes/reports/reports-event.service';
import { ReportsComponent } from './routes/reports/reports.component';
import { TransactionReportItemComponent } from './routes/reports/transaction-report/transaction-report-item/transaction-report-item.component';
import { TransactionReportSearchCriteriaComponent } from './routes/reports/transaction-report/transaction-report-search-criteria/transaction-report-search-criteria.component';
import { TransactionReportComponent } from './routes/reports/transaction-report/transaction-report.component';
import { SelectedSessionInfoComponent } from './routes/sell-camp/components/selected-session-info/selected-session-info.component';
import { SellCampCouponComponent } from './routes/sell-camp/components/sell-camp-coupon/sell-camp-coupon.component';
import { SellCampSummaryComponent } from './routes/sell-camp/components/sell-camp-summary/sell-camp-summary.component';
import { SellCampComponent } from './routes/sell-camp/sell-camp.component';
import { CampsPriviligesProviderService } from './services/camps-privileges-provider-service';
import { CampCategorySectionComponent } from './routes/manage-images/category-image-manager/camp-type-section/camp-category-section/camp-category-section.component';

@NgModule({
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateDirective,
        TranslatePipe,
        InfrastructureModule,
        UtilsModule,
        InfiniteScrollModule,
        TabsModule,
        AccordionModule,
        UiSwitchModule,
        TooltipModule,
        NgxUploaderModule,
        DropDownModule,
        BayclubsUiDatePickerModule,
        BayclubsUiTimePickerModule,
        NgxMaskDirective,
        PurchaseDescriptionPipe,
        TranslatePurchaseStatusPipe,
        BcEmployeeAuthModule,
        TestIdDirective,
    ],
    declarations: [
        DashboardComponent,
        LoginComponent,
        NavbarComponent,
        LoggedUserComponent,
        PurchaseListComponent,
        PurchaseDetailsComponent,
        MembershipSearchComponent,
        AcquiredSessionsComponent,
        PurchaseInfoComponent,
        PaymentDetailsComponent,
        AcquiredSessionItemComponent,
        PurchaseListRowComponent,
        SideMenuComponent,
        EnrollmentInfoTileComponent,
        ForbiddenComponent,
        StatisticsTilesComponent,
        StatisticsChartComponent,
        CampIconComponent,
        DailyAmountChartComponent,
        CampListComponent,
        CampSearchCriteriaComponent,
        CampListRowComponent,
        SortCampsComponent,
        PurchaseSearchCriteriaComponent,
        CampDetailsComponent,
        CampEnrollmentComponent,
        SessionSearchCriteriaComponent,
        SessionsGroupComponent,
        SessionsListComponent,
        SessionsDetailsComponent,
        DiscountsListComponent,
        DiscountDetailsComponent,
        CampCreateComponent,
        CampImportComponent,
        CampCategoryComponent,
        OptionsListComponent,
        CampBasicInfoComponent,
        CampVisibilityComponent,
        SessionsBasicInfoComponent,
        SessionDaysComponent,
        SessionDaysShiftComponent,
        DayVariantsListComponent,
        TimeVariantsListComponent,
        CampCategoryEditComponent,
        TimeVariantDetailsComponent,
        DayVariantDetailsComponent,
        CampBasicInfoEditComponent,
        CampVisibilityEditComponent,
        DiscountDetailsEditComponent,
        SessionsBasicInfoEditComponent,
        DayVariantDetailsEditComponent,
        OptionDetailsComponent,
        OptionDetailsEditComponent,
        CampCareComponent,
        CampCareDaysListComponent,
        CampCareDaysGeneratorComponent,
        DashboardFilterComponent,
        CampCareSlotsEditComponent,
        CampCareDayEditComponent,
        SessionAddComponent,
        ClubSelectComponent,
        CampCareDaysGeneratorSummaryComponent,
        CampCareDaySingleEditComponent,
        SessionEnrollmentItemComponent,
        DaysVariantEnrollmentItemComponent,
        CampCareBookingComponent,
        CampCareBookingCriteriaComponent,
        CampCareBookingCalendarComponent,
        CampCareBookingCalendarNavComponent,
        CampCareBookingCalendarBodyComponent,
        AcquiredSessionsSummaryComponent,
        PurchaseEditComponent,
        PurchaseNewSessionModalComponent,
        PurchaseEditInfoComponent,
        PurchaseEditSessionsComponent,
        PurchaseEditSessionItemComponent,
        PurchaseEditSessionsSummaryComponent,
        PurchaseEditSessionSummaryItemComponent,
        PurchaseEditSummaryComponent,
        CampPricingComponent,
        PricingMatrixComponent,
        RosterComponent,
        RosterSearchCriteriaComponent,
        RosterItemComponent,
        CouponsListComponent,
        CouponsSearchCriteriaComponent,
        CouponGenerateModalComponent,
        CouponGenerateComponent,
        CouponListRowComponent,
        CouponDetailsModalComponent,
        CouponDetailsComponent,
        ConfirmationReasonComponent,
        ReportsComponent,
        EnrollmentReportComponent,
        EnrollmentReportItemComponent,
        EnrollmentReportSearchCriteriaComponent,
        ReportSelectComponent,
        CapacityReportComponent,
        CapacityReportSearchCriteriaComponent,
        CapacityReportItemComponent,
        TransactionReportComponent,
        TransactionReportItemComponent,
        TransactionReportSearchCriteriaComponent,
        RefundedSessionsComponent,
        MembershipListComponent,
        MembershipListRowComponent,
        MembershipSearchCriteriaComponent,
        SellCampComponent,
        MemberSearchComponent,
        SellCampSummaryComponent,
        SelectedSessionInfoComponent,
        SellCampCouponComponent,
        ManageImagesComponent,
        CampOptionsSelectorComponent,
        CategoryImageManagerComponent,
        CampTypeSectionComponent,
        CampCategorySectionComponent,
    ],
    providers: [
        PurchaseApiService,
        MembershipApiService,
        SessionsWeeksService,
        CampsEnumTranslationService,
        StatisticsApiService,
        CampDefinitionApiService,
        FilterApiService,
        CampEnrollmentApiService,
        CampsDetailsFormBuilder,
        CampsDetailsFormStateService,
        CampCareApiService,
        DashboardFilterService,
        DiscountManagementApiService,
        CampManagementApiService,
        OptionManagementApiService,
        DayVariantManagementApiService,
        SessionManagementApiService,
        CampCareBookingApiService,
        CampCareBookingsCalendarEventService,
        CartApiService,
        ChildApiService,
        PaymentApiService,
        PurchaseEditEventService,
        WaiveApiService,
        RosterApiService,
        RosterEventService,
        CouponApiService,
        ReportsApiService,
        ReportsEventService,
        SellCampApiService,
        { provide: BC_PRIVILEGES_PROVIDERS, useClass: CampsPriviligesProviderService, multi: true },
    ],
    exports: [NavbarComponent, SideMenuComponent],
})
export class CoreModule {}

//application services

//application components
export { ClubSelectComponent } from './components/club-select/club-select.component';
export { MemberSearchComponent } from './components/member-search/member-search.component';
export { MembershipSearchComponent } from './components/membership-search/membership-search.component';
export { NavbarComponent } from './components/navbar/navbar.component';
export { CampCareBookingComponent } from './routes/camp-care-booking/camp-care-booking.component';
export { CampEnrollmentComponent } from './routes/camp-enrollment/camp-enrollment.component';
export { DashboardComponent } from './routes/dashboard/dashboard.component';
export { ForbiddenComponent } from './routes/forbidden/forbidden.component';
export { LoginComponent } from './routes/login/login.component';
export { PurchaseDetailsComponent } from './routes/purchase-list/purchase-details/purchase-details.component';
export { PurchaseEditComponent } from './routes/purchase-list/purchase-edit/purchase-edit.component';
export { PurchaseListComponent } from './routes/purchase-list/purchase-list.component';
export { ReportsComponent } from './routes/reports/reports.component';
export { RosterComponent } from './routes/roster/roster.component';
export {
    CampBasicInfoComponent,
    CampBasicInfoEditComponent,
    CampCategoryComponent,
    CampCategoryEditComponent,
    CampDetailsComponent,
    CampListComponent,
    CampsDetailsFormBuilder,
    CampVisibilityComponent,
    CampVisibilityEditComponent,
    DayVariantDetailsComponent,
    DayVariantDetailsEditComponent,
    DayVariantsListComponent,
    DiscountDetailsComponent,
    DiscountDetailsEditComponent,
    DiscountsListComponent,
    OptionDetailsComponent,
    OptionDetailsEditComponent,
    OptionsListComponent,
    SessionAddComponent,
    SessionDaysComponent,
    SessionsBasicInfoComponent,
    SessionsBasicInfoEditComponent,
    SessionsGroupComponent,
    SessionsListComponent,
    SideMenuComponent,
    TimeVariantDetailsComponent,
    TimeVariantsListComponent,
};
